import { gql } from '@apollo/client';

import {
  ESIGN_CONTRACT_CARD_FIELDS,
  ESIGN_CONTRACT_TEMPLATE_FIELDS,
  ESIGN_MESSAGE_TEMPLATE_FIELDS,
  ESIGN_OUTREACH_FIELDS,
} from '../fragments/esign';

/**
 * ТЗ 0076 — договоры с подписанием через Documenso.
 *
 * Имена операций намеренно НЕ совпадают с полями бэкенда
 * (`contractDocuments` → `ContractDocumentsList` и т. д.): совпадение имени
 * операции и поля схлопывает тип результата в кодгене (скилл graphql-codegen).
 *
 * Список и счётчик — два поля с ОДНИМ набором фильтров в одной операции:
 * «показано 25 из 40» обязано отвечать на тот же вопрос, что и таблица.
 */
/**
 * Что включено на сервере (`ESIGN_ENABLED`, письма). Переходный период с
 * PandaDoc: код выкатывается с выключенным флагом, и ERP обязан выглядеть как
 * раньше — кнопки создания и отправки прячутся по этому ответу.
 */
export const EsignAvailabilityQuery = gql`
  query EsignAvailability {
    esignSettings {
      enabled
      mailEnabled
      mailRedirected
      inviteVia
      shortLinksEnabled
    }
  }
`;

export const ContractDocumentsListQuery = gql`
  query ContractDocumentsList(
    $kind: String
    $status: String
    $fulfilmentStatus: String
    $groupId: ID
    $studentId: ID
    $teacherId: ID
    $createdById: ID
    $sellerId: ID
    $sentById: ID
    $search: String
    $dateFrom: Date
    $dateTo: Date
    $limit: Int
    $offset: Int
  ) {
    contractDocuments(
      kind: $kind
      status: $status
      fulfilmentStatus: $fulfilmentStatus
      groupId: $groupId
      studentId: $studentId
      teacherId: $teacherId
      createdById: $createdById
      sellerId: $sellerId
      sentById: $sentById
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      limit: $limit
      offset: $offset
    ) {
      id
      kind
      number
      status
      fulfilmentStatus
      cancelConflict
      textPending
      signerFirstName
      signerLastName
      signerEmail
      signerPhone
      signerLegalName
      groupId
      groupName
      courseName
      teacherId
      applicationId
      applicationTitle
      studentId
      agreementId
      paymentPlan {
        id
        name
      }
      currency
      listPrice
      price
      createdByName
      sellerId
      sellerName
      sentById
      sentByName
      # ТЗ 0083: ведёт ли смотрящий договор — значок ссылки в строке.
      canManage
      createdAt
      sentAt
      emailOpenedAt
      openedAt
      signedAt
      completedAt
    }
    contractDocumentsTotal(
      kind: $kind
      status: $status
      fulfilmentStatus: $fulfilmentStatus
      groupId: $groupId
      studentId: $studentId
      teacherId: $teacherId
      createdById: $createdById
      sellerId: $sellerId
      sentById: $sentById
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
    )
    # Счётчики у чипов статусов: те же фильтры, статус сервер не учитывает.
    contractDocumentStatusCounts(
      kind: $kind
      status: $status
      fulfilmentStatus: $fulfilmentStatus
      groupId: $groupId
      studentId: $studentId
      teacherId: $teacherId
      createdById: $createdById
      sellerId: $sellerId
      sentById: $sentById
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      status
      count
    }
  }
`;

/**
 * ТЗ 0106 — плитки «Подписано за 30 дней» и «Сумма подписанных за 30 дней»:
 * число подписанных (`status: completed`) и суммы `price` по валютам с
 * `signedFrom` под прочими фильтрами списка. Валюты не складываются.
 */
export const ContractSignedRecentlyQuery = gql`
  query ContractSignedRecently(
    $kind: String
    $fulfilmentStatus: String
    $groupId: ID
    $studentId: ID
    $teacherId: ID
    $createdById: ID
    $sellerId: ID
    $sentById: ID
    $search: String
    $dateFrom: Date
    $dateTo: Date
    $signedFrom: Date!
  ) {
    contractDocumentsTotal(
      kind: $kind
      status: "completed"
      fulfilmentStatus: $fulfilmentStatus
      groupId: $groupId
      studentId: $studentId
      teacherId: $teacherId
      createdById: $createdById
      sellerId: $sellerId
      sentById: $sentById
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      signedFrom: $signedFrom
    )
    contractDocumentAmounts(
      kind: $kind
      status: "completed"
      fulfilmentStatus: $fulfilmentStatus
      groupId: $groupId
      studentId: $studentId
      teacherId: $teacherId
      createdById: $createdById
      sellerId: $sellerId
      sentById: $sentById
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      signedFrom: $signedFrom
    ) {
      currency
      amount
      count
    }
  }
`;

/** Карточка одного документа — ящик «Договор» (`?contract=<id>`). */
export const ContractDocumentCardQuery = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  query ContractDocumentCard($id: ID!) {
    contractDocument(id: $id) {
      ...EsignContractCardFields
    }
  }
`;

/**
 * Свежие presigned-ссылки на PDF (живут 5 минут).
 *
 * Отдельный запрос, а не поля карточки: карточка может провисеть открытой
 * полчаса, и ссылка из кэша к клику уже мертва. Зовётся с `no-cache` в момент
 * клика.
 */
export const EsignContractPdfLinksQuery = gql`
  query EsignContractPdfLinks($id: ID!) {
    contractDocument(id: $id) {
      id
      previewPdfUrl
      signedPdfUrl
    }
  }
`;

/** Справочник тарифов для ящика «Новый договор». */
export const EsignPaymentPlansQuery = gql`
  query EsignPaymentPlans {
    paymentPlans {
      id
      code
      name
      installments
      firstDueKind
      firstDueDays
      marksStatePrefinanced
      bankInstallments
    }
  }
`;

/**
 * Что ERP подставит в черновик: прайс группы, график, следующий номер и
 * предупреждения (найден существующий студент, дубль, нет будущих занятий).
 */
export const EsignDraftDefaultsQuery = gql`
  query EsignDraftDefaults(
    $groupId: ID!
    $paymentPlanId: ID!
    $currency: String
    $email: String
    $phone: String
    $excludeContractId: ID
    $crmLeadId: ID
  ) {
    contractDraftDefaults(
      groupId: $groupId
      paymentPlanId: $paymentPlanId
      currency: $currency
      email: $email
      phone: $phone
      excludeContractId: $excludeContractId
      crmLeadId: $crmLeadId
    ) {
      listPrice
      listPriceSource
      currency
      schedule {
        n
        amount
        rule
        ruleKind
        ruleParams
      }
      nextNumberPreview
      matchedUserId
      matchedUserName
      warnings
    }
  }
`;

/**
 * ТЗ 0090 — чем заполнить «Новый договор», открытый из карточки лида
 * (`?contractNew=1&fromLead=<id строки лида>`): человек, связь с лидом Kommo,
 * курс и его ближайшие группы, тариф «Дофинансирование». Подсказка, а не
 * черновик: все проверки — у `createStudentContractDraft`.
 *
 * Имя операции ≠ имя поля (`contractDraftFromLead`): иначе codegen склеил бы
 * тип результата с типом поля.
 */
export const EsignDraftFromLeadQuery = gql`
  query EsignDraftFromLead($leadId: ID!) {
    contractDraftFromLead(leadId: $leadId) {
      leadId
      leadName
      firstName
      lastName
      email
      phone
      crmLeadId
      crmLeadBlocked
      courseId
      courseName
      courseSource
      courseRaw
      groups {
        id
        name
        startDate
      }
      suggestedGroupId
      paymentPlanId
      paymentPlanReason
      fundingCandidate {
        id
        name
        status
        isArchived
      }
      existingContracts {
        id
        number
        status
        kind
        createdAt
      }
    }
  }
`;

/**
 * Поиск группы для ящика «Новый договор» — по названию, на сервере.
 *
 * Свой запрос, а не `PaginatedGroups`: здесь нужен курс (подпись пункта) и не
 * нужны счётчики студентов и денег, которые тот тащит за собой.
 */
export const EsignGroupOptionsQuery = gql`
  query EsignGroupOptions($query: String, $first: Int) {
    allGroups(query: $query, first: $first) {
      edges {
        node {
          id
          name
          startDate
          currency
          course {
            id
            name
          }
        }
      }
    }
  }
`;

/**
 * ТЗ 0079: лиды воронки New Leads в Kommo — только чтение. Свежие изменённые
 * сверху; поиск по имени, email и телефону — на сервере.
 *
 * Kommo недоступен — не ошибка GraphQL, а `error`: форма работает руками.
 */
export const EsignCrmLeadFields = gql`
  fragment EsignCrmLeadFields on CrmLeadType {
    id
    name
    statusId
    statusName
    statusColor
    updatedAt
    responsibleName
    contactId
    firstName
    lastName
    email
    phone
    url
  }
`;

export const EsignCrmLeadsQuery = gql`
  query EsignCrmLeads($query: String, $statusId: ID, $page: Int) {
    crmLeads(query: $query, statusId: $statusId, page: $page) {
      items {
        ...EsignCrmLeadFields
      }
      hasMore
      error {
        code
        message
      }
    }
  }
  ${EsignCrmLeadFields}
`;

export const EsignCrmLeadStatusesQuery = gql`
  query EsignCrmLeadStatuses {
    crmLeadStatuses {
      items {
        id
        name
        color
      }
      error {
        code
        message
      }
    }
  }
`;

/** Подпись лида черновика — его может не быть на первой странице. */
export const EsignCrmLeadLabelQuery = gql`
  query EsignCrmLeadLabel($id: ID!) {
    crmLead(id: $id) {
      ...EsignCrmLeadFields
    }
  }
  ${EsignCrmLeadFields}
`;

/** Подпись уже выбранной группы (из `?group=<id>`), которой нет в поиске. */
/**
 * Фильтр «Группа» списка договоров: группы ВИДИМЫХ договоров. Свой источник,
 * а не `allGroups`: список читает и FinanceManager, а `allGroups` ему закрыт
 * (ошибка прав на каждой загрузке страницы). Выбранная группа приходит тем же
 * ответом (`selectedId`) — отдельный запрос подписи не нужен.
 */
export const ContractFilterGroupsQuery = gql`
  query ContractFilterGroups($query: String, $selectedId: ID, $first: Int) {
    contractGroupOptions(
      query: $query
      selectedId: $selectedId
      first: $first
    ) {
      id
      name
      startDate
      courseName
    }
  }
`;

export const EsignGroupLabelQuery = gql`
  query EsignGroupLabel($groupId: ID) {
    group(groupId: $groupId) {
      id
      name
      startDate
      currency
      course {
        id
        name
      }
    }
  }
`;

/**
 * Поиск человека для «Повторить зачисление» при `ambiguous_person`.
 *
 * Бэкенд примет `existingUserId`, только если это кандидат из ошибки, тот же
 * человек по email/телефону подписанта или студент своего юрлица, — поэтому
 * ищем среди студентов (`allStudents` скоуплен по юрлицу), а кандидаты из
 * ошибки идут первыми.
 */
export const EsignPersonOptionsQuery = gql`
  query EsignPersonOptions($query: String, $first: Int) {
    allStudents(query: $query, first: $first) {
      edges {
        node {
          id
          user {
            id
            firstName
            lastName
            email
            phone
          }
        }
      }
    }
  }
`;

/**
 * ТЗ 0077 — SMS-напоминания договора студента (секция ящика «Договор»).
 *
 * `null` — договор не студенческий или не виден пользователю. Имя операции
 * отличается от поля `contractOutreach` (скилл graphql-codegen).
 */
export const ContractOutreachStateQuery = gql`
  ${ESIGN_OUTREACH_FIELDS}
  query ContractOutreachState($contractId: ID!) {
    contractOutreach(contractId: $contractId) {
      ...EsignOutreachFields
    }
  }
`;

/**
 * Сводка SMS для строк списка договоров — ОДИН запрос на страницу, а не по
 * запросу на строку. Бэкенд берёт не больше 200 id и отвечает только по
 * видимым пользователю договорам студентов.
 */
export const ContractOutreachSummaryListQuery = gql`
  query ContractOutreachSummaryList($contractIds: [ID!]!) {
    contractOutreachSummaries(contractIds: $contractIds) {
      contractId
      smsSent
      lastSmsAt
      humanClicks
    }
  }
`;

/**
 * ТЗ 0078 — продавцы договоров.
 *
 * Без флага — продавцы видимых договоров (варианты фильтра списка);
 * `forReassign: true` — кому Admin может передать договор (активные
 * сотрудники продаж своего юрлица). Не-админу второй вариант отдаёт пустой
 * список. Имя операции отличается от поля `contractSellers`.
 */
export const ContractSellerOptionsQuery = gql`
  query ContractSellerOptions($forReassign: Boolean) {
    contractSellers(forReassign: $forReassign) {
      id
      name
      email
    }
  }
`;

/**
 * ТЗ 0083 — кто отправлял видимые договоры: варианты фильтра «Отправил».
 * Имя операции отличается от поля `contractSenders`.
 */
export const ContractSenderOptionsQuery = gql`
  query ContractSenderOptions {
    contractSenders {
      id
      name
      email
    }
  }
`;

/**
 * ТЗ 0078 — треды Gmail с подписантом договора (вкладка «Переписка»).
 *
 * Проекция узла совпадает с панелями коммуникатора: Apollo нормализует ту же
 * `ConversationType`, и бейджи не расходятся между экранами.
 */
export const ContractConversationListQuery = gql`
  query ContractConversationList($contractId: ID!) {
    contractConversations(contractId: $contractId) {
      id
      kind
      title
      displayTitle
      externalId
      mailboxEmail
      unreadCount
      unhandledCount
      status
      hasUnhandled
      lastMessageAt
    }
  }
`;

/**
 * Сводка почты для строк списка договоров — ОДИН запрос на страницу (как
 * SMS): число тредов и необработанных входящих. Бэкенд берёт не больше 200 id
 * и отвечает только по видимым договорам студентов и фирм.
 */
export const ContractMailSummaryListQuery = gql`
  query ContractMailSummaryList($contractIds: [ID!]!) {
    contractMailSummaries(contractIds: $contractIds) {
      contractId
      threads
      unhandled
      lastInboundAt
    }
  }
`;

/**
 * Ящики, с которых пользователь может написать подписанту: общие плюс свой
 * личный (свой — первым). Своя операция, а не `GmailMailboxesList`
 * коммуникатора: окну «От» нужны признаки личного ящика и его владельца.
 */
export const ContractMailboxOptionsQuery = gql`
  query ContractMailboxOptions {
    gmailMailboxes {
      id
      email
      displayName
      isPersonal
      isMine
      ownerName
    }
  }
`;

/**
 * ТЗ 0078 — шаблоны SMS и писем по договору (справочник и выбор в карточке).
 *
 * `includeInactive` действует только для Admin: остальным бэкенд отдаёт
 * включённые. Имя операции отличается от поля `messageTemplates`.
 */
export const ContractMessageTemplatesQuery = gql`
  ${ESIGN_MESSAGE_TEMPLATE_FIELDS}
  query ContractMessageTemplates(
    $channel: String
    $includeInactive: Boolean
    $topic: String
    $includeOthers: Boolean
  ) {
    messageTemplates(
      channel: $channel
      includeInactive: $includeInactive
      topic: $topic
      includeOthers: $includeOthers
    ) {
      ...EsignMessageTemplateFields
    }
  }
`;

/**
 * ТЗ 0094: письмо-приглашение так, как оно уйдёт при отправке договора
 * сейчас (тот же ящик, своя или общая версия шаблона, тот же рендер), и
 * разовая правка (`emailSubject` / `emailBody` — сырой текст с `{…}`).
 * `null` — договор не найден или не ваш. Имя операции отличается от поля.
 * ERP #133: `templateId` — выбранный в окне шаблон; `templates` — из чего
 * выбирать, `hasOwnCopy` — есть ли своя версия письма этого договора.
 */
export const EsignInvitePreviewQuery = gql`
  query EsignInvitePreview(
    $documentId: ID!
    $emailSubject: String
    $emailBody: String
    $templateId: ID
  ) {
    contractInvitePreview(
      documentId: $documentId
      emailSubject: $emailSubject
      emailBody: $emailBody
      templateId: $templateId
    ) {
      subject
      body
      rawSubject
      rawBody
      fromEmail
      fromName
      toEmail
      templateCode
      templateId
      templates {
        id
        code
        name
        namePl
        isPersonal
      }
      hasOwnCopy
      ownCopyId
      templateName
      templateNamePl
      isPersonal
      via
      editable
      canSend
      skipReason
      errorCode
      errorMessage
      errorField
    }
  }
`;

/**
 * Перепроверка 0084: может ли текущий сотрудник создавать, править и удалять
 * шаблоны темы. Правило одно — на сервере (Admin — все темы; продавец и
 * аккаунт-менеджер — только `lead_sms`); экран его не повторяет, а
 * спрашивает. Имя операции отличается от поля.
 */
export const MessageTemplatesEditableQuery = gql`
  query MessageTemplatesEditable($topic: String) {
    canEditMessageTemplates(topic: $topic)
  }
`;

/**
 * Подстановки шаблонов (`{first_name}`, `{link}`…) — справочник под полем
 * текста. ТЗ 0084: у каждой — что это, откуда берётся значение (у `{link}` —
 * куда ведёт ссылка в этой теме) и пример; русские и польские поля оба —
 * язык интерфейса знает браузер.
 */
export const ContractMessageVariablesQuery = gql`
  query ContractMessageVariables($topic: String) {
    messageTemplateVariables(topic: $topic) {
      name
      description
      descriptionRu
      descriptionPl
      source
      sourceRu
      sourcePl
      example
    }
  }
`;

/**
 * Предпросмотр SMS со своим текстом: что уйдёт, сколько сегментов, какая
 * кодировка, добавится ли хвост отписки. Ссылка в `body` — образец той же
 * длины, настоящая не раскрывается. `null` — договор не студенческий.
 */
/**
 * ТЗ 0083 — массовая SMS из списка договоров: кому уйдёт, кому нет и
 * почему, образец текста на первом договоре, которому уйдёт. Та же проверка,
 * что при отправке. `null` — выбрано больше 200 договоров.
 */
export const ContractsSmsBulkPreviewQuery = gql`
  query ContractsSmsBulkPreview($contractIds: [ID!]!, $text: String!) {
    previewContractsSms(contractIds: $contractIds, text: $text) {
      willSend
      skipped
      segmentsTotal
      rows {
        contractId
        number
        signerName
        ok
        code
        message
        segments
      }
      sample {
        body
        encoding
        segments
        maxSegments
        tooLong
        withOptOut
        available
        reason
        message
      }
    }
  }
`;

export const ContractSmsPreviewQuery = gql`
  query ContractSmsPreview($contractId: ID!, $text: String!) {
    previewContractSms(contractId: $contractId, text: $text) {
      body
      encoding
      segments
      maxSegments
      tooLong
      withOptOut
      available
      reason
      message
    }
  }
`;

/**
 * Шаблоны договоров: выбор в форме «Новый договор» (`kind: student`) и
 * справочник. Имя операции отличается от поля `contractTemplates`.
 */
export const ContractTemplateListQuery = gql`
  ${ESIGN_CONTRACT_TEMPLATE_FIELDS}
  query ContractTemplateList(
    $kind: String
    $includeInactive: Boolean
    $code: String
  ) {
    contractTemplates(
      kind: $kind
      includeInactive: $includeInactive
      code: $code
    ) {
      ...EsignContractTemplateFields
    }
  }
`;

/**
 * HTML образца договора на вымышленных данных (реквизиты школы — настоящие).
 * Показывается в песочнице `iframe srcDoc` без скриптов.
 */
export const ContractTemplateSampleQuery = gql`
  query ContractTemplateSample($id: ID!) {
    contractTemplatePreview(id: $id)
  }
`;

/**
 * ТЗ 0107 — тело шаблона для визуального редактора: html с чипами-токенами,
 * токены и палитра переменных вида. `language` — подписи чипов (ru | pl).
 * Имя операции отличается от поля `contractTemplateEditable`.
 */
export const ContractTemplateEditorSourceQuery = gql`
  ${ESIGN_CONTRACT_TEMPLATE_FIELDS}
  query ContractTemplateEditorSource($id: ID!, $language: String) {
    contractTemplateEditable(id: $id, language: $language) {
      template {
        ...EsignContractTemplateFields
      }
      html
      tokens {
        n
        kind
        role
        code
        label
        labelPl
        removable
        placement
      }
      variables {
        code
        group
        label
        labelPl
        groupLabel
        groupLabelPl
      }
    }
  }
`;

/**
 * ТЗ 0107 — образец НЕсохранённого текста редактора (только Admin): html
 * образца или `problems` с номером чипа для подсветки.
 */
export const ContractTemplateDraftSampleQuery = gql`
  query ContractTemplateDraftSample(
    $id: ID!
    $html: String!
    $tokens: JSONString
  ) {
    contractTemplatePreviewDraft(id: $id, html: $html, tokens: $tokens) {
      html
      problems {
        code
        message
        token
      }
    }
  }
`;

/**
 * ТЗ 0078 — аналитика договоров ERP. Когорта — договоры, СОЗДАННЫЕ в
 * периоде; суммы и время до подписи — по ПОДПИСАННЫМ в периоде. Суммы
 * бэкенд отдаёт только Admin и FinanceManager (`amountsVisible`).
 */
export const ContractAnalyticsReportQuery = gql`
  query ContractAnalyticsReport(
    $dateFrom: Date
    $dateTo: Date
    $sellerId: ID
    $sentById: ID
    $status: String
    $kind: String
    $course: String
    $granularity: String
  ) {
    contractAnalytics(
      dateFrom: $dateFrom
      dateTo: $dateTo
      sellerId: $sellerId
      sentById: $sentById
      status: $status
      kind: $kind
      course: $course
      granularity: $granularity
    ) {
      funnel {
        created
        sent
        opened
        signed
        enrolled
      }
      statuses {
        status
        count
      }
      tts {
        signedCount
        medianHours
        avgHours
        within24hPct
        within72hPct
      }
      sellers {
        sellerId
        name
        created
        sent
        signed
        signedInPeriod
        conversionPct
        medianHours
        amounts {
          currency
          amount
        }
      }
      courses {
        course
        created
        sent
        signed
        conversionPct
      }
      series {
        period
        created
        sent
        signed
      }
      sms {
        sent
        delivered
        failed
        humanClicks
        contractsClicked
      }
      amountsVisible
    }
  }
`;
